import dayjs from 'dayjs';

/**
 * Pure geometry for the Marketing Router timeline (Gantt) view.
 *
 * Everything works in Sydney calendar days as `YYYY-MM-DD` strings — the same
 * shape the rule records already carry — so no timezone maths leaks in and the
 * behaviour is unit-tested without rendering. Positions are returned as
 * percentages of the visible window so the component can lay out with plain CSS.
 */

export type TimelineScale = 'week' | 'month';

export const TIMELINE_SCALE_OPTIONS: Array<{ value: TimelineScale; label: string }> = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

export function isTimelineScale(value: string): value is TimelineScale {
  return TIMELINE_SCALE_OPTIONS.some((option) => option.value === value);
}

const DAY = 'YYYY-MM-DD';

export type TimelineTick = {
  /** `YYYY-MM-DD` day this column represents. */
  day: string;
  /** Short label for the axis, e.g. `Mon 8`. */
  label: string;
  /** True for Saturdays and Sundays, so the grid can tint weekends. */
  isWeekend: boolean;
};

export type TimelineWindow = {
  /** Inclusive first day shown. */
  start: string;
  /** Inclusive last day shown. */
  end: string;
  ticks: Array<TimelineTick>;
};

/** Total inclusive days between two `YYYY-MM-DD` days (min 1). */
function inclusiveDays(start: string, end: string): number {
  return Math.max(1, dayjs(end).diff(dayjs(start), 'day') + 1);
}

/**
 * The window shown for a given anchor day and scale.
 * - `week`: the Monday–Sunday week containing the anchor.
 * - `month`: the calendar month containing the anchor.
 */
export function getTimelineWindow(anchorDay: string, scale: TimelineScale): TimelineWindow {
  const anchor = dayjs(anchorDay);
  // dayjs starts its week on Sunday; normalise to Monday so weeks read the way
  // marketing expects.
  const windowStart = scale === 'week' ? mondayOf(anchor) : anchor.startOf('month');
  const windowEnd = scale === 'week' ? windowStart.add(6, 'day') : anchor.endOf('month');

  const ticks: Array<TimelineTick> = [];
  let cursor = windowStart;
  while (cursor.isBefore(windowEnd) || cursor.isSame(windowEnd, 'day')) {
    const weekday = cursor.day();
    ticks.push({
      day: cursor.format(DAY),
      label: cursor.format(scale === 'week' ? 'ddd D' : 'D'),
      isWeekend: weekday === 0 || weekday === 6,
    });
    cursor = cursor.add(1, 'day');
  }

  return { start: windowStart.format(DAY), end: windowEnd.format(DAY), ticks };
}

/** The Monday on or before the given day. */
function mondayOf(day: dayjs.Dayjs): dayjs.Dayjs {
  const weekday = day.day(); // 0 = Sunday … 6 = Saturday
  const offset = weekday === 0 ? 6 : weekday - 1;
  return day.subtract(offset, 'day').startOf('day');
}

/** Move the anchor one window forward (`+1`) or back (`-1`). */
export function shiftAnchor(anchorDay: string, scale: TimelineScale, direction: 1 | -1): string {
  const unit = scale === 'week' ? 'week' : 'month';
  return dayjs(anchorDay).add(direction, unit).format(DAY);
}

export type BarPlacement = {
  /** Left edge as a percentage of the window width. */
  leftPct: number;
  /** Width as a percentage of the window width. */
  widthPct: number;
  /** The rule starts before the window (draw a leading ◀ affordance). */
  clippedStart: boolean;
  /** The rule ends after the window (draw a trailing ▶ affordance). */
  clippedEnd: boolean;
};

/**
 * Where a rule's bar sits inside the window, or `null` when the rule's date
 * range does not overlap the window at all.
 */
export function getBarPlacement(
  rule: { start_date: string; end_date: string },
  window: Pick<TimelineWindow, 'start' | 'end'>,
): BarPlacement | null {
  if (rule.end_date < window.start || rule.start_date > window.end) {
    return null;
  }

  const clampedStart = rule.start_date < window.start ? window.start : rule.start_date;
  const clampedEnd = rule.end_date > window.end ? window.end : rule.end_date;

  const totalDays = inclusiveDays(window.start, window.end);
  const startIndex = dayjs(clampedStart).diff(dayjs(window.start), 'day');
  const spanDays = inclusiveDays(clampedStart, clampedEnd);

  return {
    leftPct: (startIndex / totalDays) * 100,
    widthPct: (spanDays / totalDays) * 100,
    clippedStart: rule.start_date < window.start,
    clippedEnd: rule.end_date > window.end,
  };
}
